import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {BasePanelPage} from "../../BasePanelPage";
import {Messages} from "../../../../../Messages";
import {subscribeFlowState} from "../../../../inline-subscription/SubscribeFlowState";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {Link} from "../../../../../../stem-core/src/ui/UIPrimitives";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../../PanelConstants";
import {CreditCardIcon} from "../../../../../ui/CreditCardIcon";
import {SubscriptionApplyPolicy} from "../../../../../../client/state/SubscriptionStore";
import {LabeledCheckbox} from "../../../../../ui/Checkbox";
import {MerchantCheckbox} from "../../../../../widget/ui/MerchantCheckbox";
import {MerchantButton} from "../../../../../common/MerchantButton";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {styleRule, styleRuleInherit} from "../../../../../../stem-core/src/decorators/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {SubscribeReviewOrderPanelStyle} from "../SubscribeReviewOrderPanel";
import {addressController} from "../../../../inline-subscription/AddressController";
import {subscribePlanController} from "../../../../inline-subscription/SubscribePlanController";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {UsingBlinkDefaultPaymentMethod} from "../../../../../common/UsingBlinkDefaultPaymentMethod";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {PaymentMethodStore} from "../../../../../State";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";
import {paymentMethodController} from "../../../../inline-donation/PaymentMethodController";
import {getPaymentMethodEmptySubscriptionMessage} from "../../../../../../blink-sdk/messaging/MessagingUtils";


class EditSubscriptionPanelStyle extends SubscribeReviewOrderPanelStyle {
    @styleRuleInherit
    reviewItem = {
        ":first-child": {
            marginTop: 0,
        }
    };

    @styleRuleInherit
    reviewItemTitleContainer = {
        marginBottom: 8,
    };

    @styleRule
    checkbox = {
        color: "inherit",
        fontSize: "inherit",
        marginBottom: 8,
    };

    @styleRule
    cardIcon = {
        display: "inline-block",
        marginRight: 12,
    };

    @styleRule
    cardDetails = {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    };
}

@autoredraw(PaymentMethodStore)
@registerStyle(EditSubscriptionPanelStyle)
export class EditSubscriptionPanel extends BasePanelPage {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.manageSubscription,
        };
    }

    isPrimaryButtonDisabled() {
        const subscription = iFrameUserDataService.getActiveSubscription();
        return subscribePlanController.userHasEquivalentSubscriptionToSelected() &&
            subscription.getShippingAddress() === addressController.selectedAddress &&
            subscription.getPaymentMethod() === paymentMethodController.selectedPaymentMethod &&
            (subscription.getDiscount()?.getCode() || null) === subscribePlanController.discountCode;
    }

    renderCheckboxAndPriceInfo() {
        const {selectedOffer, applyPolicy} = subscribePlanController;
        const instantPayInfo = subscribePlanController.getDiscountInfoOnInstantChangePolicy(selectedOffer);
        const nextCyclePayInfo = subscribePlanController.getDiscountInfo(selectedOffer);
        const activeSubscription = iFrameUserDataService.getActiveSubscription();
        const selectedBillingPlan = selectedOffer == null ? activeSubscription.billingPlan : selectedOffer.billingPlan;

        if (!instantPayInfo) {
            return null;
        }

        if (subscribePlanController.userHasEquivalentSubscriptionToSelected() && selectedBillingPlan.formatPrice(nextCyclePayInfo.finalPrice).toString() === activeSubscription.getPrice().toString()) {
            return null;
        }

        const {styleSheet} = this;
        const paidUntil = activeSubscription.getPaidUntil();
        const nextBillingDate = activeSubscription.getNextBillingDate();

        const paymentInfoMessageArgs = [
            selectedBillingPlan.formatPrice(nextCyclePayInfo.finalPrice),
            selectedBillingPlan.getCycleDuration(true),
            nextBillingDate.format("MMM DD, YYYY")
        ];
        const updatePaymentInfo = applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE
            ? [
                Messages.updateAtEndCyclePaymentInfo(...paymentInfoMessageArgs),
                subscribePlanController.userHasEquivalentSubscriptionToSelected() ? "" : Messages.updateAtEndCycleChangeOffer,
            ].join("")
            : Messages.updateInstantlyPaymentInfo(selectedBillingPlan.formatPrice(instantPayInfo.finalPrice), ...paymentInfoMessageArgs);

        let checkboxElement = null;

        if (instantPayInfo.finalPrice !== nextCyclePayInfo.finalPrice) {
            checkboxElement = <LabeledCheckbox className={styleSheet.checkbox} testId="applyPolicyCheckbox"
                                               onChange={enabled => {
                                                   subscribePlanController.applyPolicy = enabled ? SubscriptionApplyPolicy.NEXT_BILLING_CYCLE :
                                                       SubscriptionApplyPolicy.INSTANTLY;
                                                   this.redraw();
                                               }}
                                               CheckboxClass={MerchantCheckbox} initialValue={applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE}
                                               label={Messages.updateAtEndCycleCheckbox(paidUntil.format("MMM DD, YYYY"))}/>;
        }

        return [
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentInfoSection">
                {checkboxElement}
                <MessageElement message={updatePaymentInfo}/>
            </div>
        ];
    }

    render() {
        const {styleSheet} = this;
        const shippingAddress = addressController.selectedAddress;
        const preferredPaymentMethod = paymentMethodController.selectedPaymentMethod;
        const {selectedOffer} = subscribePlanController;
        const activeSubscription = iFrameUserDataService.getActiveSubscription();
        const selectedCoverage = selectedOffer == null ? activeSubscription.coverage : selectedOffer.coverage;
        const selectedBillingPlan = selectedOffer == null ? activeSubscription.billingPlan : selectedOffer.billingPlan;
        let discountInfo = subscribePlanController.getDiscountInfo(selectedOffer);
        if (!discountInfo) {
            subscribePlanController.updateEditPricesFromExistingSubscription();
            discountInfo = subscribePlanController.getDiscountInfo(selectedOffer);
            if (!discountInfo) {
                return null;
            }
        }
        const finalPrice = selectedBillingPlan.formatPrice(discountInfo.finalPrice);
        const subscriptionPlanArgs = [selectedCoverage.name, finalPrice, selectedBillingPlan.getCycleDuration(true)];
        const subscriptionPlanText = subscribePlanController.userHasEquivalentSubscriptionToSelected()
            ? Messages.editSubscriptionPlanDescription(...subscriptionPlanArgs)
            : (discountInfo.finalPrice === discountInfo.discountedBeforeTaxesPrice
                ? Messages.editSubscriptionChangePlanDescription(...subscriptionPlanArgs)
                : Messages.editSubscriptionChangePlanWithShippingIncludedDescription(...subscriptionPlanArgs));

        return [
            <div className={styleSheet.reviewItem} testId="subscriptionPlanSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.subscriptionPlan}</div>
                    <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.changePlan} value={Messages.change} className={styleSheet.link}
                          testId="changeButton"/>
                </div>
                <div className={""}>
                    <MessageElement message={subscriptionPlanText} />
                </div>
            </div>,
            (selectedCoverage.requiresAddress || (selectedOffer != null && selectedOffer.shouldCollectAddress())) ? [
                <div className={styleSheet.lineSeparator}/>,
                <div className={styleSheet.reviewItem} testId="addressSection">
                    <div className={styleSheet.reviewItemTitleContainer}>
                        <div className={styleSheet.reviewItemTitle}>{Messages.address}</div>
                        <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.address} value={Messages.edit}
                              className={styleSheet.link} testId="editButton"/>
                    </div>
                    <div>
                        <div className={styleSheet.nameOnAddress}>{shippingAddress?.getRecipientName()}</div>
                        <div className={styleSheet.addressDetails}>{shippingAddress}</div>
                    </div>
                </div>
            ] : null,
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentMethodSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.paymentMethod}</div>
                    <Link href={INLINE_EDIT_SUBSCRIPTION_URLS.paymentDetails}
                          value={preferredPaymentMethod ? Messages.edit : Messages.addNewCard}
                          className={styleSheet.link} testId="editButton"/>
                </div>
                {
                    preferredPaymentMethod ?
                    <div className={styleSheet.cardDetails}>
                        <CreditCardIcon className={styleSheet.cardIcon} brand={preferredPaymentMethod.getBrand()}/>
                        <MessageElement message={Messages.usingCardSubscribe(
                            preferredPaymentMethod.getBrand(), preferredPaymentMethod.getLastDigits())}/>
                    </div> : (
                        iFrameUserDataService.hasPrimaryPaymentMethod() && !iFrameMerchantService.isCDSPaymentMethod()?
                            <UsingBlinkDefaultPaymentMethod /> :
                            <div className={styleSheet.cardDetails}>
                                <div>{getPaymentMethodEmptySubscriptionMessage(activeSubscription)}</div>
                            </div>
                    )
                }
            </div>,
            this.renderCheckboxAndPriceInfo(),
            <MerchantButton disabled={this.isPrimaryButtonDisabled()} className={styleSheet.button}
                            onClick={() => subscribeFlowState.changeSubscription()}>
                {Messages.saveChanges}
            </MerchantButton>,
            this.isPrimaryButtonDisabled() ?
            <div className={styleSheet.center}>
                <div onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)}
                     className={styleSheet.secondaryButton}>{Messages.cancelSubscription}</div>
            </div> : null
        ];
    }

    onMount() {
        super.onMount();
        this.attachListener(addressController, () => this.redraw());
        this.attachListener(subscribePlanController, () => {
            this.redraw();
        });
    }
}
